import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useMemo, useState } from 'react'

import {
  type ReceivedRow,
  type VwPageFilter,
  useDeleteReceivedMutation,
  useGetReceivedListQuery,
  useLazyGetDnPrintPreviewQuery,
  useLazyGetRnPrintPreviewQuery,
} from '../../store/api/virtualWarehouseApi'
import { usePreference } from '../../hooks/usePreference'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import ReceivedFormDialog from './ReceivedFormDialog'
import DnPrintDialog from './DnPrintDialog'
import RnPrintDialog from './RnPrintDialog'
import {
  DataframeBody,
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { titleCaseName } from '../../utils/text'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

type FilterKind = 'search'

/** Freeze the RDN No. identifier column while scrolling horizontally. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['rdnNo'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { rdnNo: 120 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { rdnNo: 100 }

const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'number'
type ColumnDef = {
  key: keyof ReceivedRow
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
  proper?: boolean // title-case VALUE cột tên riêng IN HOA (display-only)
}

/** Column order + captions verbatim from the Access Received datasheet (0..41). */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'rdnNo', label: 'RDN No' },
  { key: 'dateIn', label: 'Date in', kind: 'date' },
  { key: 'doNo', label: 'DO No' },
  { key: 'truckNo', label: 'Truck No' },
  { key: 'fromSupplier', label: 'From Supplier' },
  { key: 'supplierName', label: 'Supplier name', proper: true },
  { key: 'toClient', label: 'To Client' },
  { key: 'clientName', label: 'Client name', proper: true },
  { key: 'warehouseCode', label: 'Warehouse Code' },
  { key: 'warehouseName', label: 'Warehouse Name', proper: true },
  { key: 'quality', label: 'Quality', filter: 'checkboxes' },
  { key: 'announcedQuantity', label: 'Announced Quantity', kind: 'number', align: 'right' },
  { key: 'noOfBag', label: 'No of Bag', kind: 'number', align: 'right' },
  { key: 'vgmQuantity', label: 'VGM Quantiy', kind: 'number', align: 'right' },
  { key: 'mc', label: 'MC', kind: 'number', align: 'right' },
  { key: 'bb', label: 'BB', kind: 'number', align: 'right' },
  { key: 'brown', label: 'Brown', kind: 'number', align: 'right' },
  { key: 'cherry', label: 'Cherry', kind: 'number', align: 'right' },
  { key: 'overdry', label: 'Overdry', kind: 'number', align: 'right' },
  { key: 'fm', label: 'FM', kind: 'number', align: 'right' },
  { key: 'black', label: 'Black', kind: 'number', align: 'right' },
  { key: 'brokens', label: 'Brokens', kind: 'number', align: 'right' },
  { key: 'foreign', label: 'Foreign', kind: 'number', align: 'right' },
  { key: 'insect', label: 'Insect', kind: 'number', align: 'right' },
  { key: 'immature', label: 'Immature', kind: 'number', align: 'right' },
  { key: 'old', label: 'Old', kind: 'number', align: 'right' },
  { key: 'mouldy', label: 'Mouldy', kind: 'number', align: 'right' },
  { key: 'white', label: 'White', kind: 'number', align: 'right' },
  { key: 's18', label: 'S18', kind: 'number', align: 'right' },
  { key: 's17', label: 'S17', kind: 'number', align: 'right' },
  { key: 's16', label: 'S16', kind: 'number', align: 'right' },
  { key: 's15', label: 'S15', kind: 'number', align: 'right' },
  { key: 's14', label: 'S14', kind: 'number', align: 'right' },
  { key: 's13', label: 'S13', kind: 'number', align: 'right' },
  { key: 's12', label: 'S12', kind: 'number', align: 'right' },
  { key: 'underS12', label: '<S12', kind: 'number', align: 'right' },
  { key: 'deductionKg', label: 'Deduction (Kg)', kind: 'number', align: 'right' },
  { key: 'deductionVnd', label: 'Deduction (VND)', kind: 'number', align: 'right' },
  { key: 'paymentQuantity', label: 'Payment Quantity', kind: 'number', align: 'right' },
  { key: 'status', label: 'Status', filter: 'checkboxes' },
  { key: 'note', label: 'Note', wrap: true, width: 220 },
  { key: 'deductionOther', label: 'Deduction Other', kind: 'number', align: 'right' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessNumber = (value: number): string =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const sumNumericColumn = (rows: ReceivedRow[], key: keyof ReceivedRow): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}


export default function VirtualWarehouseReceivedView() {
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const snack = useSnackbar()
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()
  const [confirmDelete, setConfirmDelete] = useState<ReceivedRow | null>(null)

  // Dialog mount state (dialogs built by another agent — wired later).
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit' | 'copy'; id: number | null } | null>(null)
  const [dnPrint, setDnPrint] = useState<{ open: boolean; title: string; html: string; loading: boolean }>({
    open: false, title: '', html: '', loading: false,
  })
  const [rnPrint, setRnPrint] = useState<{ open: boolean; title: string; html: string; loading: boolean }>({
    open: false, title: '', html: '', loading: false,
  })
  const [deleteReceived, { isLoading: deleting }] = useDeleteReceivedMutation()
  const [triggerDnPreview] = useLazyGetDnPrintPreviewQuery()
  const [triggerRnPreview] = useLazyGetRnPrintPreviewQuery()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths('virtual-warehouse-received.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof ReceivedRow> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const closeFilterPopover = () => filterPopover.close()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: keyof ReceivedRow; position: ColumnFilterAnchorPosition } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'virtual_warehouse',
    prefKey: 'columns',
    subKey: 'received',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'virtual-warehouse-received.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  // Shared "Date in" filter lives in the page-level common block; read it here
  // and apply server-side to the Received list (the shell owns the UI + Export).
  const pageFilter = usePreference<VwPageFilter>('virtual_warehouse', 'page_filter')
  const { dateInFrom, dateInTo } = pageFilter.value

  const { data: list, isLoading, isFetching, error } = useGetReceivedListQuery(
    { page: 1, pageSize: ALL_ROWS_PAGE_SIZE, dateInFrom, dateInTo },
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo(() => list?.items ?? [], [list])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number')
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const minTableWidth = Math.max(960, visibleColumns.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof ReceivedRow) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<ReceivedRow | null>(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  )

  const requireRow = (): ReceivedRow | null => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return selectedRow
  }

  const openForm = (mode: 'new' | 'edit' | 'copy') => {
    if (!canEdit) return
    if (mode !== 'new' && !requireRow()) return
    setFormState({ mode, id: mode === 'new' ? null : selectedRow?.id ?? null })
  }

  const handlePrintDn = async () => {
    const row = requireRow()
    if (!row?.rdnNo) return
    setDnPrint({ open: true, title: `Print Preview - DN (${row.rdnNo})`, html: '', loading: true })
    try {
      const res = await triggerDnPreview(row.rdnNo).unwrap()
      setDnPrint({ open: true, title: res.title, html: res.html, loading: false })
    } catch (err) {
      const e = err as { status?: number; data?: { detail?: string } }
      snack.warning(e?.status === 422 ? e.data?.detail || 'Cannot print this record' : 'Failed to generate print preview.')
      setDnPrint({ open: false, title: '', html: '', loading: false })
    }
  }

  const handlePrintRn = async () => {
    const row = requireRow()
    if (!row?.rdnNo) return
    setRnPrint({ open: true, title: `Print Preview - RN (${row.rdnNo})`, html: '', loading: true })
    try {
      const res = await triggerRnPreview(row.rdnNo).unwrap()
      setRnPrint({ open: true, title: res.title, html: res.html, loading: false })
    } catch (err) {
      const e = err as { status?: number; data?: { detail?: string } }
      snack.warning(e?.status === 422 ? e.data?.detail || 'Cannot print this record' : 'Failed to generate print preview.')
      setRnPrint({ open: false, title: '', html: '', loading: false })
    }
  }


  const handleDeleteClick = () => {
    const row = requireRow()
    if (!row) return
    setConfirmDelete(row)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteReceived(confirmDelete.id).unwrap()
      snack.success(`Received ${confirmDelete.rdnNo ?? ''} deleted`)
      if (selectedId === confirmDelete.id) setSelectedId(null)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete received record failed'))
    }
  }

  const cellValue = (row: ReceivedRow, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.proper && typeof val === 'string') return titleCaseName(val)
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={() => openForm('new')}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={() => openForm('edit')}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={() => openForm('copy')}
              startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              title="Copy"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={handleDeleteClick}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              title="Delete"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canEdit}
              onClick={() => void handlePrintDn()}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Pr-DN"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Pr-</Box>DN
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canEdit}
              onClick={() => void handlePrintRn()}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Pr-RN"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Pr-</Box>RN
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Received records.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="RDN no, DO no, supplier..."
              label="Search received records"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState tone="error" title="Could not load Received records" description="Please refresh the page or contact the system administrator if the issue continues." />
          ) : isLoading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title="No Received record found" description="Try adjusting the table filters above." />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', ...(isFrozen ? {} : { position: 'relative' }) }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5, bgcolor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}`, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: ReceivedRow) =>
                visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const val = cellValue(row, col)
                  const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  const wrap = col.wrap || getWidth(key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const rowId = Number.isFinite(idx) ? rows[idx]?.id : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowId != null && rowId === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => { if (rowId != null) toggleSelect(rowId) }}
                    />
                  )
                },
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Received"
        message={confirmDelete ? `Do you want to delete Received record RDN No = '${confirmDelete.rdnNo ?? ''}' ?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />

      <FuturesColumnsMenu controller={columnsCtrl} />

      {formState && (
        <ReceivedFormDialog
          open
          mode={formState.mode}
          recordId={formState.id}
          canEdit={canEdit}
          dateInFrom={dateInFrom}
          dateInTo={dateInTo}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}
      <DnPrintDialog
        open={dnPrint.open}
        title={dnPrint.title}
        html={dnPrint.html}
        loading={dnPrint.loading}
        canEdit={canEdit}
        onClose={() => setDnPrint({ open: false, title: '', html: '', loading: false })}
      />
      <RnPrintDialog
        open={rnPrint.open}
        title={rnPrint.title}
        html={rnPrint.html}
        loading={rnPrint.loading}
        canEdit={canEdit}
        onClose={() => setRnPrint({ open: false, title: '', html: '', loading: false })}
      />
    </Box>
  )
}
